import React from "react";
import { Loader2, Mic, Pause, Play, RotateCcw } from "lucide-react";
import { getExpertPalette } from "../utils/expertStyles";
import { expertOptions } from "../constants/experts";
import VerdictMeter from "./VerdictMeter";
import { Verdict } from "../utils/verdict";
import { DebateSource, visibleSources } from "../utils/sources";
import SourcesDisclosure from "./SourcesList";

interface AudioDebateViewProps {
  debateTopic: string | null;
  debateImages?: string[];
  councilProgress: any[];
  chaosProgress: any[];
  councilFinal: string | null;
  facilitatorDone: boolean;
  /** True only when both the facilitator text AND audio playback are finished */
  audioFinished: boolean;
  isLoading: boolean;
  selectedExperts: string[];
  expandedExperts: Record<string, boolean>;
  onToggleExpert: (expert: string) => void;
  // Audio-specific props
  currentSpeaker: string | null;
  currentSpeakerLabel: string | null;
  currentPhase: string | null;
  completedTurns: string[]; // "speaker-phase" keys that have finished speaking
  // Playback controls
  playbackState: "playing" | "paused" | "idle";
  onPause: () => void;
  onResume: () => void;
  onReplay: () => void;
  /** The panel verdict on a question with fixed answers. */
  verdict?: Verdict | null;
  /** Links the experts relied on. */
  sources?: DebateSource[];
}

/** Map phase to human-readable label */
function phaseLabel(phase: string | null): string {
  switch (phase) {
    case "initial":
      return "Initial Answer";
    case "feedback":
      return "Peer Feedback";
    case "chaos":
      return "Chaos Attack!";
    case "revision":
      return "Revised Answer";
    case "final":
      return "Final Synthesis";
    default:
      return "";
  }
}

/** Expert emoji icons for the avatars */
const expertEmoji: Record<string, string> = {
  creative: "🎨",
  logic: "🧠",
  coding: "💻",
  ethics: "⚖️",
  research: "🔬",
  nutrition: "🥗",
  fitness: "💪",
  parenting: "👶",
  finance: "💰",
  psychology: "🧩",
  history: "📜",
  legal: "⚖️",
  medical: "🏥",
  religion: "🕊️",
  decision: "🎯",
  statistician: "📊",
};

/**
 * AudioDebateView — Conference-call style circle layout.
 * No text — only avatars arranged in a circle with speaking indicators.
 */
const AudioDebateView: React.FC<AudioDebateViewProps> = ({
  debateTopic,
  debateImages = [],
  facilitatorDone,
  audioFinished,
  isLoading,
  selectedExperts,
  currentSpeaker,
  currentSpeakerLabel,
  currentPhase,
  completedTurns,
  playbackState,
  onPause,
  onResume,
  onReplay,
  verdict = null,
  sources = [],
}) => {
  // All participants: selected experts + chaos + facilitator
  const allParticipants = [
    ...selectedExperts.map((e) => ({
      id: e,
      label:
        expertOptions.find((o) => o.value === e)?.label ??
        e.charAt(0).toUpperCase() + e.slice(1),
      type: "expert" as const,
    })),
    { id: "chaos", label: "Chaos Agent", type: "chaos" as const },
    { id: "facilitator", label: "Facilitator", type: "facilitator" as const },
  ];

  const totalCount = allParticipants.length;
  const radius = Math.max(110, 45 + totalCount * 20);
  const hasAnyoneSpoken = completedTurns.length > 0 || currentSpeaker !== null;

  return (
    <div className={`flex flex-col items-center justify-center min-h-[55vh] select-none px-2 ${playbackState === "paused" ? "is-paused" : ""}`}>
      {/* Compact topic banner */}
      {debateTopic && (
        <div className="mb-4 text-center max-w-sm">
          <div className="text-xs font-medium text-muted mb-0.5">
            Audio debate
          </div>
          <div className="text-sm font-medium text-content leading-snug line-clamp-2">
            &ldquo;{debateTopic}&rdquo;
          </div>
          {debateImages.length > 0 && (
            <div className="mt-2 flex justify-center gap-2">
              {debateImages.map((img, idx) => (
                <img
                  key={idx}
                  src={img}
                  alt={`Debate attachment ${idx + 1}`}
                  className="h-16 w-16 rounded-md border border-line object-cover"
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Conference circle ──────────────────────────────────────────── */}
      <div
        className="relative mx-auto"
        style={{ width: radius * 2 + 90, height: radius * 2 + 90 }}
      >
        {/* Centre hub — current status + playback controls */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center max-w-[170px]">
            {currentSpeaker && playbackState !== "paused" ? (
              <>
                {/* Sound bars */}
                <div className="flex justify-center mb-1.5">
                  <div className="flex items-end gap-[3px] h-5">
                    {[...Array(5)].map((_, i) => (
                      <div
                        key={i}
                        className="w-[3px] bg-success-solid rounded-full animate-sound-wave"
                        style={{
                          animationDelay: `${i * 0.12}s`,
                          height: "100%",
                        }}
                      />
                    ))}
                  </div>
                </div>
                <div className="text-xs font-semibold text-success uppercase tracking-wide">
                  Speaking
                </div>
                <div className="text-sm font-semibold text-content mt-0.5 leading-tight">
                  {currentSpeakerLabel || currentSpeaker}
                </div>
                <div className="text-xs text-success mt-0.5">
                  {phaseLabel(currentPhase)}
                </div>
              </>
            ) : playbackState === "paused" ? (
              <>
                <Pause className="mx-auto mb-1 text-warning" size={20} />
                <div className="text-xs font-semibold text-warning">
                  Paused
                </div>
                {currentSpeakerLabel && (
                  <div className="text-xs text-muted mt-0.5 truncate">
                    {currentSpeakerLabel} — {phaseLabel(currentPhase)}
                  </div>
                )}
              </>
            ) : (isLoading || playbackState === "playing") && !audioFinished ? (
              <>
                <Loader2
                  className="mx-auto mb-1 text-muted animate-spin"
                  size={18}
                />
                <div className="text-xs text-muted">
                  {!hasAnyoneSpoken ? "Preparing debate…" : "Next speaker…"}
                </div>
              </>
            ) : audioFinished ? (
              <>
                <div className="text-2xl mb-0.5">✅</div>
                <div className="text-xs font-semibold text-muted">
                  Debate Complete
                </div>
              </>
            ) : (
              <>
                <Mic className="mx-auto mb-1 text-muted" size={22} />
                <div className="text-xs text-muted">Ready</div>
              </>
            )}

            {/* ── Playback control buttons ──────────────────────────────── */}
            {(isLoading || hasAnyoneSpoken || facilitatorDone) && (
              <div className="flex items-center justify-center gap-2 mt-2.5">
                {/* Replay (restart from beginning) — only when there's something to replay */}
                {hasAnyoneSpoken && (
                  <button
                    type="button"
                    onClick={onReplay}
                    title="Replay from start"
                    className="p-1.5 rounded-md text-muted hover:text-accent hover:bg-accent-soft transition-colors active:scale-95"
                  >
                    <RotateCcw size={16} />
                  </button>
                )}

                {/* Pause / Play toggle */}
                {playbackState === "paused" ? (
                  <button
                    type="button"
                    onClick={onResume}
                    title="Resume"
                    className="p-2 rounded-full bg-success-solid text-on-solid shadow-sm hover:bg-success-solid transition-colors active:scale-95"
                  >
                    <Play size={18} className="ml-0.5" />
                  </button>
                ) : (isLoading || playbackState === "playing") &&
                  !audioFinished ? (
                  <button
                    type="button"
                    onClick={onPause}
                    title="Pause"
                    className="p-2 rounded-full bg-warning-solid text-on-solid shadow-sm hover:bg-warning-solid transition-colors active:scale-95"
                  >
                    <Pause size={18} />
                  </button>
                ) : null}
              </div>
            )}
          </div>
        </div>

        {/* Participant avatars arranged in a circle */}
        {allParticipants.map((p, index) => {
          const angle = (2 * Math.PI * index) / totalCount - Math.PI / 2;
          const x = radius * Math.cos(angle);
          const y = radius * Math.sin(angle);

          const isSpeaking = currentSpeaker === p.id;
          const hasSpoken = completedTurns.some((t) =>
            t.startsWith(`${p.id}-`),
          );
          const isWaiting =
            isLoading && !isSpeaking && !hasSpoken && !audioFinished;

          const palette = getExpertPalette(p.id, selectedExperts);
          const ringCls = p.type === "expert" ? palette.ring : p.type === "chaos" ? "ring-danger-line" : "ring-focus";
          const bgCls = p.type === "expert"
            ? isSpeaking ? palette.activeBg : palette.badgeBg
            : p.type === "chaos"
              ? isSpeaking ? "bg-danger-soft" : "bg-danger-soft"
              : isSpeaking ? "bg-accent-soft" : "bg-accent-soft";
          const emoji =
            p.type === "chaos"
              ? "⚡"
              : p.type === "facilitator"
                ? "🎙️"
                : expertEmoji[p.id] || "🤖";

          const size = isSpeaking ? 68 : 56;

          return (
            <div
              key={p.id}
              className="absolute flex flex-col items-center transition-all duration-500 ease-out"
              style={{
                left: `calc(50% + ${x}px - ${size / 2}px)`,
                top: `calc(50% + ${y}px - ${size / 2}px)`,
                zIndex: isSpeaking ? 20 : 10,
              }}
            >
              {/* Avatar */}
              <div
                className={`
                  relative rounded-full flex items-center justify-center
                  transition-all duration-300 shadow-sm
                  ${bgCls}
                  ${isSpeaking ? `ring-[3px] ${ringCls} shadow-lg` : ""}
                  ${hasSpoken && !isSpeaking ? "opacity-60" : ""}
                  ${isWaiting ? "opacity-30" : ""}
                `}
                style={{ width: size, height: size }}
              >
                {/* Ping ring */}
                {isSpeaking && (
                  <div
                    className={`absolute inset-0 rounded-full border-2 ${
                      p.type === "chaos"
                        ? "border-danger-line"
                        : p.type === "facilitator"
                          ? "border-accent-line"
                          : palette.border
                    } animate-ping opacity-20`}
                    style={{ width: size, height: size }}
                  />
                )}

                <span
                  className={`select-none transition-all duration-300 ${isSpeaking ? "text-2xl" : "text-xl"}`}
                >
                  {emoji}
                </span>

                {/* Mini sound bars on avatar when speaking */}
                {isSpeaking && (
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-end gap-[2px] h-2.5">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="w-[2px] bg-success-solid rounded-full animate-sound-wave"
                        style={{
                          animationDelay: `${i * 0.15}s`,
                          height: "100%",
                        }}
                      />
                    ))}
                  </div>
                )}

                {/* Done badge */}
                {hasSpoken && !isSpeaking && (
                  <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-success-solid flex items-center justify-center shadow">
                    <svg
                      className="w-2 h-2 text-on-solid"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                      strokeWidth={3}
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                )}
              </div>

              {/* Name */}
              <div className="mt-1 text-center" style={{ width: 72 }}>
                <div
                  className={`text-xs font-semibold leading-tight truncate ${
                    isSpeaking
                      ? p.type === "expert" ? palette.accentText : "text-content"
                      : hasSpoken
                        ? "text-muted"
                        : p.type === "chaos"
                          ? "text-danger"
                          : p.type === "facilitator"
                            ? "text-accent"
                            : palette.accentText
                  }`}
                >
                  {p.label}
                </div>
                {isSpeaking && (
                  <div className="text-xs text-success font-medium mt-0.5 truncate">
                    {phaseLabel(currentPhase)}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* ── Phase progress bar ────────────────────────────────────────── */}
      {(isLoading || facilitatorDone) && (
        <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
          {(
            [
              ["initial", "Answers"],
              ["feedback", "Feedback"],
              ["chaos", "Chaos"],
              ["revision", "Revisions"],
              ["final", "Summary"],
            ] as const
          ).map(([phase, label]) => {
            const isActive = currentPhase === phase;
            const isDone =
              phase === "final"
                ? facilitatorDone
                : phase === "chaos"
                  ? completedTurns.some((t) => t.startsWith("chaos-"))
                  : selectedExperts.every((e) =>
                      completedTurns.includes(`${e}-${phase}`),
                    );
            return (
              <div
                key={phase}
                className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold border transition-all duration-300 ${
                  isActive
                    ? "bg-success-soft border-success-line text-success shadow-sm"
                    : isDone
                      ? "bg-subtle border-line text-muted"
                      : "bg-surface border-line text-muted"
                }`}
              >
                {isActive && (
                  <div className="flex items-end gap-[1px] h-2">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="w-[2px] bg-success-solid rounded-full animate-sound-wave"
                        style={{
                          animationDelay: `${i * 0.12}s`,
                          height: "100%",
                        }}
                      />
                    ))}
                  </div>
                )}
                {isDone && !isActive && (
                  <svg
                    className="w-2.5 h-2.5 text-success"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={3}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                )}
                {label}
              </div>
            );
          })}
        </div>
      )}

      {verdict && (
        <section aria-labelledby="audio-verdict-title" className="mx-auto mt-6 w-full max-w-xl border-t border-line pt-4">
          <h2 id="audio-verdict-title" className="mb-2 text-label font-medium text-muted">Panel verdict</h2>
          <VerdictMeter verdict={verdict} />
        </section>
      )}

      {facilitatorDone && visibleSources(sources).length > 0 && (
        <div className="mx-auto mt-6 w-full max-w-xl border-t border-line pt-3">
          <SourcesDisclosure sources={visibleSources(sources)} />
        </div>
      )}

      {/* Empty state */}
      {!isLoading && !audioFinished && !debateTopic && (
        <div className="mt-4 text-muted text-center text-xs">
          Start an audio debate to hear expert responses.
        </div>
      )}
    </div>
  );
};

export default AudioDebateView;
